import React, { useState } from "react";
import {
  profile,
  summary,
  talkingPoints,
  experience,
  rivaPath,
  featuredProjects,
  earlierProjects,
  skills,
  certifications,
  education,
} from "../assets/content";
import { useLineProgress } from "./hooks";

const skillIcons = {
  "Languages & Frameworks": "fas fa-code",
  "Cloud & Integrations": "fas fa-cloud",
  Data: "fas fa-database",
  "Testing & Quality": "fas fa-vial",
  "Tools & Practices": "fas fa-tools",
  "Frontend (supporting)": "fas fa-desktop",
};

function SectionTitle({ eyebrow, title }) {
  return (
    <div className="section-title reveal">
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
    </div>
  );
}

export function About() {
  return (
    <section className="section" id="about">
      <div className="container">
        <SectionTitle eyebrow="About" title="Backend engineer who owns it end to end" />
        <div className="about-grid">
          <div className="about-text reveal">
            {summary.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
          <div className="card reveal">
            <h3 className="card-title">
              <i className="fas fa-project-diagram"></i> Technical depth
            </h3>
            <ul className="check-list">
              {talkingPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Experience() {
  const timelineRef = useLineProgress();
  return (
    <section className="section section-alt" id="experience">
      <div className="container">
        <SectionTitle eyebrow="Experience" title="Where I've shipped" />
        <div className="timeline" ref={timelineRef}>
          {experience.map((job, jobIndex) => (
            <article className="timeline-item reveal" key={job.company}>
              <div className="timeline-dot" aria-hidden="true"></div>
              <div className="card">
                <header className="job-header">
                  <div>
                    <h3>{job.role}</h3>
                    <div className="job-company">{job.company}</div>
                  </div>
                  <div className="job-dates">
                    <span>{job.dates}</span>
                    <span>{job.place}</span>
                  </div>
                </header>
                {job.intro && <p className="job-intro">{job.intro}</p>}
                {jobIndex === 0 && (
                  <ol className="career-path">
                    {rivaPath.map((step) => (
                      <li key={step.team}>
                        <strong>{step.team}</strong>
                        <span>{step.detail}</span>
                      </li>
                    ))}
                  </ol>
                )}
                <ul className="check-list">
                  {job.highlights.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Projects() {
  return (
    <section className="section" id="projects">
      <div className="container">
        <SectionTitle eyebrow="Projects" title="Selected work on GitHub" />
        <div className="project-grid">
          {featuredProjects.map((project, index) => (
            <article
              className={`card project-card reveal ${index === 0 ? "is-featured" : ""}`}
              key={project.name}
            >
              <div className="project-top">
                <i className="far fa-folder-open project-icon"></i>
                <a
                  href={project.github}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${project.name} on GitHub`}
                >
                  <i className="fab fa-github"></i>
                </a>
              </div>
              <span className="eyebrow">{project.tagline}</span>
              <h3>
                <a href={project.github} target="_blank" rel="noreferrer">
                  {project.name}
                </a>
              </h3>
              <p>{project.description}</p>
              <ul className="tags">
                {project.tech.map((tech) => (
                  <li key={tech}>{tech}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        <div className="earlier reveal">
          <h3>Earlier web projects (2021)</h3>
          <ul>
            {earlierProjects.map((project) => (
              <li key={project.name}>
                <a href={project.github} target="_blank" rel="noreferrer">
                  {project.name}
                </a>
                <span>{project.tech}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export function Skills() {
  return (
    <section className="section section-alt" id="skills">
      <div className="container">
        <SectionTitle eyebrow="Skills" title="Tools I use every day" />
        <div className="skills-grid">
          {skills.map((skill) => (
            <div className="card reveal" key={skill.group}>
              <h3 className="card-title">
                <i className={skillIcons[skill.group] || "fas fa-check"}></i>{" "}
                {skill.group}
              </h3>
              <ul className="tags">
                {skill.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CertBadge({ src }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className="cert-fallback" aria-hidden="true">
        <i className="fas fa-award"></i>
      </span>
    );
  }
  return <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />;
}

export function Certifications() {
  return (
    <section className="section" id="certifications">
      <div className="container">
        <SectionTitle eyebrow="Certifications & education" title="Credentials" />
        <div className="cert-grid">
          {certifications.map((cert) => (
            <a
              className="card cert-card reveal"
              key={cert.name}
              href={require(`../assets/certificates/${cert.file}`).default}
              target="_blank"
              rel="noreferrer"
            >
              <CertBadge src={cert.badge} />
              <div>
                <h3>{cert.name}</h3>
                <span className="cert-year">
                  {cert.year} <i className="fas fa-external-link-alt"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
        <div className="edu-grid">
          {education.map((item) => (
            <div className="card edu-card reveal" key={item.name}>
              <i className="fas fa-graduation-cap"></i>
              <div>
                <h3>{item.name}</h3>
                <span>
                  {item.school}, {item.year}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Contact() {
  return (
    <section className="section section-alt" id="contact">
      <div className="container">
        <div className="contact-card reveal">
          <span className="eyebrow">Contact</span>
          <h2>Hiring for a backend or .NET role?</h2>
          <p>
            I'm based in {profile.location}. Email is the fastest way to reach
            me, and I'm also on LinkedIn.
          </p>
          <div className="hero-actions contact-actions">
            <a className="btn btn-primary" href={`mailto:${profile.email}`}>
              <i className="fas fa-envelope"></i> {profile.email}
            </a>
            <a
              className="btn btn-ghost"
              href={profile.linkedin}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fab fa-linkedin-in"></i> LinkedIn
            </a>
            <a
              className="btn btn-ghost"
              href={profile.github}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fab fa-github"></i> GitHub
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="site-footer">
      <div className="container">
        <span>
          © {new Date().getFullYear()} {profile.name}
        </span>
        <a href="#top">
          Back to top <i className="fas fa-arrow-up"></i>
        </a>
      </div>
    </footer>
  );
}
